'use client';

import React from 'react';
import { Container } from 'components/shared';
import BentoGrid, { BentoItemProps } from 'components/shared/bento/Bento';
import { useTranslations } from 'next-intl';
import { UHLesMillsSubtitle, UHLesMillsTitle } from './typography';
import ImagePlaceholder from './ImagePlaceholder';
import ProgramsCarousel from './ProgramsCarousel';
import { getLesMillsContent } from './content';
import styles from './Integration.module.css';

const Integration = () => {
  const t = useTranslations('uhXLesMills');
  const { integrationItems } = React.useMemo(() => getLesMillsContent(t), [t]);

  const items: BentoItemProps[] = integrationItems.map((item, idx) => ({
    title: item.title,
    description: item.description,
    layout: item.layout,
    colSpanLg: item.colSpanLg,
    padBottom: idx === integrationItems.length - 1 ? 24 : 0,
    padBottomDesktop: idx === integrationItems.length - 1 ? 96 : undefined,
    image: item.carousel ? (
      <div className={styles.carouselWrap}>
        <ProgramsCarousel
          items={item.carousel}
          ariaLabel={t('a11y.programsCarouselLabel')}
        />
      </div>
    ) : item.image ? (
      item.layout === 'row' ? (
        item.imageMobile ? (
          <picture>
            <source media="(min-width: 768px)" srcSet={item.image} />
            <img
              className={styles.naturalFitImage}
              src={item.imageMobile}
              alt={item.title}
            />
          </picture>
        ) : (
          <img
            className={styles.naturalFitImage}
            src={item.image}
            alt={item.title}
          />
        )
      ) : item.imageMobile ? (
        <picture>
          <source media="(min-width: 768px)" srcSet={item.image} />
          <img
            className={styles.heightFitImage}
            src={item.imageMobile}
            alt={item.title}
          />
        </picture>
      ) : (
        <img
          className={styles.heightFitImage}
          src={item.image}
          alt={item.title}
        />
      )
    ) : (
      <div className={styles.bentoImage}>
        <ImagePlaceholder
          label={item.imageLabel}
          ratio={item.layout === 'row' ? '16/9' : '4/3'}
          radius={12}
        />
      </div>
    ),
  }));

  return (
    <section
      className={styles.section}
      role="region"
      aria-labelledby="lm-integration-heading"
    >
      <Container>
        <div className={styles.heading}>
          <UHLesMillsTitle id="lm-integration-heading">
            {t('integration.heading')}
          </UHLesMillsTitle>
          <UHLesMillsSubtitle>{t('integration.subtitle')}</UHLesMillsSubtitle>
        </div>
        <BentoGrid items={items} />
      </Container>
    </section>
  );
};

export default Integration;
